import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getShops, getShopBookings, updateBookingStatus, getShopHomeBookings, updateHomeBookingStatus } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import Toast from "../components/Toast";

function getTodayDate() { return new Date().toISOString().split("T")[0]; }
function getTomorrowDate() { const t = new Date(); t.setDate(t.getDate() + 1); return t.toISOString().split("T")[0]; }
function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  let hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}
function getDaysLeft(endDate) {
  if (!endDate) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(endDate); end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
}

export default function SalonDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [shop, setShop] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [homeBookings, setHomeBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [mainView, setMainView] = useState("salon");
  const [loading, setLoading] = useState(true);
  const [shopChecked, setShopChecked] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Salon date filter (now matches home style) ──
  const [salonFilterDate, setSalonFilterDate] = useState("today");
  const [salonCustomDate, setSalonCustomDate] = useState("");

  // ── Home filters ──
  const [homeFilterDate, setHomeFilterDate] = useState("all");
  const [homeCustomDate, setHomeCustomDate] = useState("");
  const [homeStatusFilter, setHomeStatusFilter] = useState("all");

  useEffect(() => {
    const fetchMyShop = async () => {
      try {
        const res = await getShops({});
        const myShop = res.data.find((s) => Number(s.owner_id) === Number(user.id));
        if (myShop) {
          setShop(myShop);
          setShopChecked(true);
          fetchBookings(myShop.id, getTodayDate());
          fetchHomeBookings(myShop.id);
        } else {
          navigate("/create-shop", { replace: true });
        }
      } catch {
        setError("Could not load shop data.");
        setLoading(false);
        setShopChecked(true);
      }
    };
    fetchMyShop();
  }, [user.id]);

  const fetchBookings = async (shopId, date) => {
    setLoading(true);
    try {
      // pass date only if not "all"
      const res = await getShopBookings(shopId, date || undefined);
      setBookings(res.data);
    } catch { setBookings([]); }
    finally { setLoading(false); }
  };

  const fetchHomeBookings = async (shopId) => {
    try {
      const res = await getShopHomeBookings(shopId);
      setHomeBookings(res.data.bookings || []);
    } catch { setHomeBookings([]); }
  };

  const handleSalonDateFilter = (filter, customDate) => {
    setSalonFilterDate(filter);
    if (!shop) return;
    if (filter === "all") fetchBookings(shop.id, null);
    else if (filter === "today") fetchBookings(shop.id, getTodayDate());
    else if (filter === "tomorrow") fetchBookings(shop.id, getTomorrowDate());
    else if (filter === "custom" && customDate) fetchBookings(shop.id, customDate);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      setBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, status: newStatus } : b));
    } catch { showToast("Could not update booking status.", "error"); }
  };

 const handleHomeStatusUpdate = async (bookingId, newStatus) => {
  try {
    const res = await updateHomeBookingStatus(bookingId, newStatus);
    setHomeBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, status: newStatus } : b));
    showToast(`Home booking ${newStatus}!`);
    if (res?.data?.booking?.customer_whatsapp_url) {
      window.open(res.data.booking.customer_whatsapp_url, "_blank");
    }
  } catch { showToast("Could not update home booking status.", "error"); }
};

  // ── Salon stats ──
  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    revenue: bookings.filter((b) => b.status === "completed").reduce((sum, b) => sum + parseFloat(b.amount || 0), 0),
  };

  // ── Filtered salon bookings by status ──
  const filteredBookings = bookings.filter((b) => {
    if (activeTab === "all") return true;
    return b.status === activeTab;
  });

  // ── Filtered home bookings ──
  const getFilteredHomeBookings = () => {
    let filtered = [...homeBookings];
    if (homeFilterDate === "today") filtered = filtered.filter((b) => b.preferred_date?.slice(0, 10) === getTodayDate());
    else if (homeFilterDate === "tomorrow") filtered = filtered.filter((b) => b.preferred_date?.slice(0, 10) === getTomorrowDate());
    else if (homeFilterDate === "custom" && homeCustomDate) filtered = filtered.filter((b) => b.preferred_date?.slice(0, 10) === homeCustomDate);
    if (homeStatusFilter !== "all") filtered = filtered.filter((b) => b.status === homeStatusFilter);
    return filtered;
  };
  const filteredHomeBookings = getFilteredHomeBookings();

  // ── Home stats ──
  const homeStats = {
    total: homeBookings.length,
    pending: homeBookings.filter((b) => b.status === "pending").length,
    confirmed: homeBookings.filter((b) => b.status === "confirmed").length,
    completed: homeBookings.filter((b) => b.status === "completed").length,
    revenue: filteredHomeBookings.filter((b) => b.status === "completed").reduce((sum, b) => sum + parseFloat(b.total_price || 0), 0),
  };

  // ── Salon revenue label ──
  const salonRevenueLabel = () => {
    if (salonFilterDate === "all") return "All Time";
    if (salonFilterDate === "today") return "Today";
    if (salonFilterDate === "tomorrow") return "Tomorrow";
    return salonCustomDate || "Selected Date";
  };

  if (!shopChecked) return (
    <div style={styles.loadingScreen}>
      <div style={styles.loadingSpinner}>💈</div>
      <p style={styles.loadingText}>Loading...</p>
    </div>
  );

  if (!shop) return null;

  const daysLeft = getDaysLeft(shop.subscription_end_date);
  const isExpiringSoon = shop.is_subscribed && daysLeft <= 7 && daysLeft > 0;
  const isExpired = !shop.is_subscribed;
  const isFreeTrial = shop.is_subscribed && daysLeft > 0;

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <h1 style={styles.headerTitle}>✂️ Dashboard</h1>
            <p style={styles.headerSub}>Welcome, {user?.name?.split(" ")[0]}!</p>
          </div>
          <div style={styles.headerBtns}>
            <button onClick={() => navigate(`/subscribe/${shop?.id}`)} style={styles.headerBtnGreen}>💳 Subscribe</button>
            <button onClick={() => navigate("/coupons")} style={styles.headerBtnOrange}>🎟️ Coupons</button>
            <button onClick={() => navigate(`/edit-shop/${shop?.id}`)} style={styles.headerBtnRed}>✏️ Edit</button>
          </div>
        </div>
        <div style={styles.viewToggleRow}>
          <button onClick={() => setMainView("salon")} style={{ ...styles.viewToggleBtn, ...(mainView === "salon" ? styles.viewToggleBtnActive : {}) }}>
            💈 Salon Bookings
          </button>
          <button onClick={() => setMainView("home")} style={{ ...styles.viewToggleBtn, ...(mainView === "home" ? styles.viewToggleBtnActiveGreen : {}) }}>
            🏠 Home Bookings
            {homeStats.pending > 0 && <span style={styles.pendingDot}>{homeStats.pending}</span>}
          </button>
        </div>
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Banners */}
        {isExpired && (
          <div style={styles.expiredBanner}>
            <div>
              <p style={styles.expiredTitle}>🚫 Subscription Expired!</p>
              <p style={styles.expiredText}>Customers cannot book appointments. Subscribe to start receiving bookings again!</p>
            </div>
            <button onClick={() => navigate(`/subscribe/${shop?.id}`)} style={styles.subscribeNowBtn}>Subscribe Now →</button>
          </div>
        )}
        {isExpiringSoon && (
          <div style={styles.expiringSoonBanner}>
            <div>
              <p style={styles.expiringTitle}>⚠️ {daysLeft === 1 ? "Last day!" : `${daysLeft} days left!`}</p>
              <p style={styles.expiringText}>Expires on <strong>{new Date(shop.subscription_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</strong>. Renew now!</p>
            </div>
            <button onClick={() => navigate(`/subscribe/${shop?.id}`)} style={styles.renewNowBtn}>Renew Now →</button>
          </div>
        )}
        {isFreeTrial && !isExpiringSoon && (
          <div style={styles.trialBanner}>
            <p style={styles.trialText}>
              🎉 Free Trial Active — <strong style={{ color: "#4caf50" }}>{daysLeft} days remaining</strong>
              {" "}(valid till {new Date(shop.subscription_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })})
            </p>
          </div>
        )}

        {/* Shop Banner */}
        <div style={styles.shopBanner}>
          <div style={styles.shopBannerLeft}>
            <div style={styles.shopAvatar}>✂️</div>
            <div>
              <p style={styles.shopName}>{shop.name}</p>
              <p style={styles.shopCity}>📍 {shop.address}, {shop.city}</p>
            </div>
          </div>
          <div style={{ ...styles.subBadge, background: shop.is_subscribed ? "rgba(76,175,80,0.15)" : "rgba(255,152,0,0.15)", color: shop.is_subscribed ? "#4caf50" : "#ff9800", border: `1px solid ${shop.is_subscribed ? "rgba(76,175,80,0.3)" : "rgba(255,152,0,0.3)"}` }}>
            {shop.is_subscribed ? `✅ Active (${daysLeft}d left)` : "⚠️ Expired"}
          </div>
        </div>

        {/* ── SALON BOOKINGS VIEW ── */}
        {mainView === "salon" && (
          <>
            {/* Unified filter card — now matches home style with All button */}
            <div style={styles.filterCard}>
              <div style={styles.filterRow}>
                <div style={styles.filterGroup}>
                  <span style={styles.filterLabel}>📅 Date</span>
                  <div style={styles.filterBtns}>
                    {[
                      { key: "all", label: "All" },
                      { key: "today", label: "Today" },
                      { key: "tomorrow", label: "Tomorrow" },
                      { key: "custom", label: "Pick Date" },
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => handleSalonDateFilter(f.key, salonCustomDate)}
                        style={{ ...styles.filterBtn, ...(salonFilterDate === f.key ? styles.filterBtnActive : {}) }}
                      >
                        {f.label}
                      </button>
                    ))}
                    {salonFilterDate === "custom" && (
                      <input
                        type="date"
                        value={salonCustomDate}
                        onChange={(e) => {
                          setSalonCustomDate(e.target.value);
                          handleSalonDateFilter("custom", e.target.value);
                        }}
                        style={styles.filterDateInput}
                      />
                    )}
                  </div>
                </div>
                <div style={styles.filterGroup}>
                  <span style={styles.filterLabel}>🔖 Status</span>
                  <div style={styles.filterBtns}>
                    {[
                      { key: "all", label: `All (${stats.total})` },
                      { key: "pending", label: `Pending (${stats.pending})` },
                      { key: "confirmed", label: `Confirmed (${stats.confirmed})` },
                      { key: "completed", label: `Done (${stats.completed})` },
                    ].map((tab) => (
                      <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ ...styles.filterBtn, ...(activeTab === tab.key ? styles.filterBtnActive : {}) }}>{tab.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
              <StatCard icon="📋" label="Total" value={stats.total} color="#e94560" />
              <StatCard icon="⏳" label="Pending" value={stats.pending} color="#ff9800" />
              <StatCard icon="✅" label="Confirmed" value={stats.confirmed} color="#4fc3f7" />
              <StatCard icon="🎉" label="Completed" value={stats.completed} color="#4caf50" />
            </div>

            {/* Salon Revenue */}
            <div style={styles.revenueCard}>
              <span style={styles.revenueIcon}>💰</span>
              <div>
                <p style={styles.revenueAmount}>₹{stats.revenue.toFixed(2)}</p>
                <p style={styles.revenueLabel}>Salon Revenue ({salonRevenueLabel()})</p>
              </div>
            </div>

            {loading ? (
              <div style={styles.loadingBox}>
                {[1, 2, 3].map((i) => <div key={i} style={styles.skeletonCard}><div style={styles.skeletonLine} /><div style={{ ...styles.skeletonLine, width: "60%" }} /></div>)}
              </div>
            ) : filteredBookings.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ fontSize: "40px" }}>📭</p>
                <p style={styles.emptyText}>No bookings for {salonRevenueLabel()}</p>
              </div>
            ) : (
              filteredBookings.map((booking) => <BookingCard key={booking.id} booking={booking} onStatusUpdate={handleStatusUpdate} />)
            )}
          </>
        )}

        {/* ── HOME BOOKINGS VIEW ── */}
        {mainView === "home" && (
          <>
            <div style={styles.filterCard}>
              <div style={styles.filterRow}>
                <div style={styles.filterGroup}>
                  <span style={styles.filterLabel}>📅 Date</span>
                  <div style={styles.filterBtns}>
                    {[
                      { key: "all", label: "All" },
                      { key: "today", label: "Today" },
                      { key: "tomorrow", label: "Tomorrow" },
                      { key: "custom", label: "Pick Date" },
                    ].map((f) => (
                      <button key={f.key} onClick={() => setHomeFilterDate(f.key)} style={{ ...styles.filterBtn, ...(homeFilterDate === f.key ? styles.filterBtnActiveGreen : {}) }}>{f.label}</button>
                    ))}
                    {homeFilterDate === "custom" && (
                      <input type="date" value={homeCustomDate} onChange={(e) => setHomeCustomDate(e.target.value)} style={styles.filterDateInput} />
                    )}
                  </div>
                </div>
                <div style={styles.filterGroup}>
                  <span style={styles.filterLabel}>🔖 Status</span>
                  <div style={styles.filterBtns}>
                    {[
                      { key: "all", label: `All (${homeStats.total})` },
                      { key: "pending", label: `Pending (${homeStats.pending})` },
                      { key: "confirmed", label: `Confirmed (${homeStats.confirmed})` },
                      { key: "completed", label: `Done (${homeStats.completed})` },
                    ].map((f) => (
                      <button key={f.key} onClick={() => setHomeStatusFilter(f.key)} style={{ ...styles.filterBtn, ...(homeStatusFilter === f.key ? styles.filterBtnActiveGreen : {}) }}>{f.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.statsGrid}>
              <StatCard icon="🏠" label="Total" value={homeStats.total} color="#4f8ef7" />
              <StatCard icon="⏳" label="Pending" value={homeStats.pending} color="#ff9800" />
              <StatCard icon="✅" label="Confirmed" value={homeStats.confirmed} color="#4fc3f7" />
              <StatCard icon="🎉" label="Completed" value={homeStats.completed} color="#4caf50" />
            </div>

            <div style={styles.homeRevenueCard}>
              <span style={styles.revenueIcon}>🏠</span>
              <div>
                <p style={styles.homeRevenueAmount}>₹{homeStats.revenue.toFixed(2)}</p>
                <p style={styles.revenueLabel}>Home Service Revenue ({homeFilterDate === "all" ? "All Time" : homeFilterDate === "today" ? "Today" : homeFilterDate === "tomorrow" ? "Tomorrow" : homeCustomDate || "Selected Date"})</p>
              </div>
            </div>

            {homeBookings.length > 0 && (
              <p style={styles.homeResultsCount}>Showing {filteredHomeBookings.length} of {homeBookings.length} home bookings</p>
            )}

            {homeBookings.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ fontSize: "40px" }}>🏠</p>
                <p style={styles.emptyText}>No home booking requests yet</p>
                <p style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "8px" }}>Enable home service from Edit Shop → 🏠 Home tab</p>
              </div>
            ) : filteredHomeBookings.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ fontSize: "40px" }}>📭</p>
                <p style={styles.emptyText}>No home bookings match this filter</p>
              </div>
            ) : (
              filteredHomeBookings.map((booking) => <HomeBookingCard key={booking.id} booking={booking} onStatusUpdate={handleHomeStatusUpdate} />)
            )}
          </>
        )}
      </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

function HomeBookingCard({ booking, onStatusUpdate }) {
  return (
    <div style={{ ...styles.card, borderLeft: "3px solid #4caf50" }}>
      <div style={styles.cardHeader}>
        <div style={styles.customerRow}>
          <div style={{ ...styles.avatar, background: "#4caf50" }}>{booking.customer_name?.charAt(0).toUpperCase()}</div>
          <div>
            <p style={styles.customerName}>{booking.customer_name}</p>
            <p style={styles.customerPhone}>📞 {booking.customer_phone}</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      <div style={styles.divider} />
      <div style={styles.detailsGrid}>
        <DetailItem icon="✂️" label="Services" value={booking.service_names || "—"} />
        <DetailItem icon="💰" label="Total" value={`₹${booking.total_price}`} />
        <DetailItem icon="📅" label="Date" value={new Date(booking.preferred_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} />
        <DetailItem icon="🕐" label="Preferred Time" value={booking.preferred_time} />
      </div>
      <div style={styles.addressBox}>📍 <strong>Address:</strong> {booking.customer_address}</div>
      {booking.customer_note && <div style={styles.notesBox}>📝 {booking.customer_note}</div>}
      <div style={styles.advanceBox}>
        💳 Advance paid: <strong style={{ color: "#4caf50" }}>₹{booking.advance_amount}</strong>
        &nbsp;• Collect at door: <strong style={{ color: "var(--accent)" }}>₹{(booking.total_price - booking.advance_amount).toFixed(2)}</strong>
      </div>
      <div style={styles.actionRow}>
        {booking.status === "pending" && (<><button onClick={() => onStatusUpdate(booking.id, "confirmed")} style={styles.confirmBtn}>✅ Confirm Visit</button><button onClick={() => onStatusUpdate(booking.id, "cancelled")} style={styles.cancelBtn}>❌ Cancel</button></>)}
        {booking.status === "confirmed" && (<><button onClick={() => onStatusUpdate(booking.id, "completed")} style={styles.completeBtn}>🎉 Mark Complete</button><button onClick={() => onStatusUpdate(booking.id, "no_show")} style={styles.noShowBtn}>🚫 No Show</button></>)}
        {booking.status === "completed" && <div style={styles.doneMsg}>🎉 Service completed at customer's home</div>}
        {booking.status === "cancelled" && <div style={styles.cancelledMsg}>❌ Cancelled</div>}
        {booking.status === "no_show" && <div style={styles.noShowMsg}>🚫 Customer not found</div>}
      </div>
    </div>
  );
}

function BookingCard({ booking, onStatusUpdate }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.customerRow}>
          <div style={styles.avatar}>{booking.customer_name?.charAt(0).toUpperCase()}</div>
          <div>
            <p style={styles.customerName}>{booking.customer_name}</p>
            <p style={styles.customerPhone}>📞 {booking.customer_phone}</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      <div style={styles.divider} />
      <div style={styles.detailsGrid}>
        <DetailItem icon="✂️" label="Service" value={booking.service_name} />
        <DetailItem icon="⏱" label="Duration" value={`${booking.duration} mins`} />
        <DetailItem icon="🕐" label="Time" value={formatTime(booking.booking_time)} />
        <DetailItem icon="💰" label="Collect" value={`₹${booking.amount}`} />
      </div>
      {booking.notes && <div style={styles.notesBox}>📝 {booking.notes}</div>}
      <div style={styles.actionRow}>
        {booking.status === "pending" && (<><button onClick={() => onStatusUpdate(booking.id, "confirmed")} style={styles.confirmBtn}>✅ Confirm</button><button onClick={() => onStatusUpdate(booking.id, "cancelled")} style={styles.cancelBtn}>❌ Cancel</button></>)}
        {booking.status === "confirmed" && (<><button onClick={() => onStatusUpdate(booking.id, "completed")} style={styles.completeBtn}>🎉 Complete</button><button onClick={() => onStatusUpdate(booking.id, "no_show")} style={styles.noShowBtn}>🚫 No Show</button></>)}
        {booking.status === "completed" && <div style={styles.doneMsg}>🎉 Collected ₹{booking.amount}</div>}
        {booking.status === "cancelled" && <div style={styles.cancelledMsg}>❌ Cancelled</div>}
        {booking.status === "no_show" && <div style={styles.noShowMsg}>🚫 No Show</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending:   { color: "#ff9800", bg: "rgba(255,152,0,0.15)",   label: "⏳ Pending" },
    confirmed: { color: "#4fc3f7", bg: "rgba(79,195,247,0.15)",  label: "✅ Confirmed" },
    completed: { color: "#4caf50", bg: "rgba(76,175,80,0.15)",   label: "🎉 Completed" },
    cancelled: { color: "#e94560", bg: "rgba(233,69,96,0.15)",   label: "❌ Cancelled" },
    no_show:   { color: "#ab47bc", bg: "rgba(171,71,188,0.15)",  label: "🚫 No Show" },
  };
  const c = config[status] || config.pending;
  return <span style={{ ...styles.badge, color: c.color, background: c.bg, border: `1px solid ${c.color}33` }}>{c.label}</span>;
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ ...styles.statCard, borderTop: `3px solid ${color}` }}>
      <p style={{ fontSize: "20px", margin: "0 0 6px" }}>{icon}</p>
      <p style={{ ...styles.statValue, color }}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

function DetailItem({ icon, label, value }) {
  return (
    <div>
      <p style={styles.detailLabel}>{icon} {label}</p>
      <p style={styles.detailValue}>{value}</p>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  loadingScreen: { minHeight: "100vh", background: "var(--bg-primary)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" },
  loadingSpinner: { fontSize: "48px" },
  loadingText: { color: "var(--text-muted)", fontSize: "16px" },
  header: { background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)", padding: "16px 20px 0" },
  headerInner: { maxWidth: "800px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", paddingBottom: "12px" },
  headerTitle: { color: "white", margin: "0 0 2px", fontSize: "1.4rem", fontWeight: "800" },
  headerSub: { color: "var(--text-muted)", margin: 0, fontSize: "13px" },
  headerBtns: { display: "flex", gap: "8px", flexWrap: "wrap" },
  headerBtnGreen: { background: "rgba(76,175,80,0.15)", border: "1px solid rgba(76,175,80,0.4)", color: "#4caf50", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" },
  headerBtnOrange: { background: "rgba(255,152,0,0.15)", border: "1px solid rgba(255,152,0,0.4)", color: "#ff9800", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" },
  headerBtnRed: { background: "rgba(233,69,96,0.15)", border: "1px solid rgba(233,69,96,0.4)", color: "var(--accent)", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px" },
  viewToggleRow: { maxWidth: "800px", margin: "0 auto", display: "flex" },
  viewToggleBtn: { flex: 1, padding: "12px", border: "none", background: "none", cursor: "pointer", fontSize: "13px", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" },
  viewToggleBtnActive: { color: "var(--accent)", fontWeight: "bold", borderBottom: "2px solid var(--accent)" },
  viewToggleBtnActiveGreen: { color: "#4caf50", fontWeight: "bold", borderBottom: "2px solid #4caf50" },
  pendingDot: { background: "#ff9800", color: "white", fontSize: "10px", fontWeight: "bold", padding: "1px 6px", borderRadius: "10px", minWidth: "18px", textAlign: "center" },
  container: { maxWidth: "800px", margin: "0 auto", padding: "16px" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  expiredBanner: { background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.4)", borderRadius: "12px", padding: "16px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  expiredTitle: { color: "var(--accent)", fontWeight: "bold", fontSize: "15px", margin: "0 0 4px" },
  expiredText: { color: "var(--text-muted)", fontSize: "13px", margin: 0, lineHeight: 1.5 },
  subscribeNowBtn: { background: "var(--accent)", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 },
  expiringSoonBanner: { background: "rgba(255,152,0,0.1)", border: "1px solid rgba(255,152,0,0.4)", borderRadius: "12px", padding: "16px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  expiringTitle: { color: "#ff9800", fontWeight: "bold", fontSize: "15px", margin: "0 0 4px" },
  expiringText: { color: "var(--text-muted)", fontSize: "13px", margin: 0, lineHeight: 1.5 },
  renewNowBtn: { background: "#ff9800", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 },
  trialBanner: { background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: "10px", padding: "12px 16px", marginBottom: "16px" },
  trialText: { color: "#4caf50", fontSize: "13px", margin: 0 },
  shopBanner: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", border: "1px solid var(--border)" },
  shopBannerLeft: { display: "flex", gap: "12px", alignItems: "center" },
  shopAvatar: { width: "44px", height: "44px", background: "rgba(233,69,96,0.2)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", border: "1px solid rgba(233,69,96,0.3)" },
  shopName: { fontWeight: "700", margin: "0 0 3px", fontSize: "15px", color: "white" },
  shopCity: { color: "var(--text-muted)", margin: 0, fontSize: "12px" },
  subBadge: { padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold" },
  filterCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", border: "1px solid var(--border)" },
  filterRow: { display: "flex", flexDirection: "column", gap: "12px" },
  filterGroup: { display: "flex", alignItems: "flex-start", gap: "10px", flexWrap: "wrap", width: "100%", minWidth: 0 },  filterLabel: { color: "var(--text-muted)", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", paddingTop: "7px", minWidth: "70px" },
  filterBtns: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", maxWidth: "100%", overflowX: "auto", paddingBottom: "2px" },  
  filterBtn: { padding: "7px 10px", border: "1px solid var(--border)", borderRadius: "20px", background: "var(--bg-secondary)", cursor: "pointer", fontSize: "11px", color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0 },  filterBtnActive: { border: "1px solid var(--accent)", color: "var(--accent)", fontWeight: "bold", background: "rgba(233,69,96,0.1)" },
  filterBtnActiveGreen: { border: "1px solid #4caf50", color: "#4caf50", fontWeight: "bold", background: "rgba(76,175,80,0.1)" },
  filterDateInput: { padding: "6px 10px", borderRadius: "8px", fontSize: "12px", cursor: "pointer", background: "var(--bg-secondary)", color: "white", border: "1px solid var(--border)" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "10px" },
  statCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid var(--border)" },
  statValue: { fontSize: "1.6rem", fontWeight: "900", margin: "0 0 4px" },
  statLabel: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  revenueCard: { background: "linear-gradient(135deg, rgba(233,69,96,0.15), rgba(233,69,96,0.05))", borderRadius: "12px", padding: "16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px", border: "1px solid rgba(233,69,96,0.2)" },
  revenueIcon: { fontSize: "32px" },
  revenueAmount: { fontSize: "1.8rem", fontWeight: "900", color: "var(--accent)", margin: "0 0 2px" },
  revenueLabel: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  homeRevenueCard: { background: "linear-gradient(135deg, rgba(76,175,80,0.15), rgba(76,175,80,0.05))", borderRadius: "12px", padding: "16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px", border: "1px solid rgba(76,175,80,0.2)" },
  homeRevenueAmount: { fontSize: "1.8rem", fontWeight: "900", color: "#4caf50", margin: "0 0 2px" },
  homeResultsCount: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "12px", textAlign: "right" },
  loadingBox: { display: "flex", flexDirection: "column", gap: "12px" },
  skeletonCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "20px", border: "1px solid var(--border)" },
  skeletonLine: { height: "12px", background: "var(--bg-hover)", borderRadius: "4px", marginBottom: "10px", width: "100%" },
  emptyState: { textAlign: "center", padding: "40px", color: "var(--text-muted)" },
  emptyText: { fontSize: "15px", marginTop: "8px" },
  card: { background: "var(--bg-card)", borderRadius: "14px", padding: "18px", marginBottom: "12px", border: "1px solid var(--border)" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" },
  customerRow: { display: "flex", gap: "12px", alignItems: "center" },
  avatar: { width: "40px", height: "40px", borderRadius: "50%", background: "var(--accent)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "bold", flexShrink: 0 },
  customerName: { fontWeight: "bold", fontSize: "14px", margin: "0 0 2px", color: "white" },
  customerPhone: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold" },
  divider: { height: "1px", background: "var(--border)", margin: "12px 0" },
  detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" },
  detailLabel: { color: "var(--text-muted)", fontSize: "11px", margin: "0 0 3px" },
  detailValue: { fontWeight: "600", fontSize: "14px", margin: 0, color: "white" },
  notesBox: { background: "var(--bg-secondary)", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", color: "var(--text-secondary)", marginBottom: "12px" },
  addressBox: { background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.2)", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", color: "var(--text-secondary)", marginBottom: "10px" },
  advanceBox: { background: "var(--bg-secondary)", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", color: "var(--text-secondary)", marginBottom: "12px" },
  actionRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  confirmBtn: { padding: "8px 16px", background: "rgba(79,195,247,0.1)", border: "1px solid rgba(79,195,247,0.4)", color: "#4fc3f7", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "12px" },
  completeBtn: { padding: "8px 16px", background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.4)", color: "#4caf50", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "12px" },
  cancelBtn: { padding: "8px 16px", background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.4)", color: "var(--accent)", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "12px" },
  noShowBtn: { padding: "8px 16px", background: "rgba(171,71,188,0.1)", border: "1px solid rgba(171,71,188,0.4)", color: "#ab47bc", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "12px" },
  doneMsg: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "8px 14px", borderRadius: "8px", fontSize: "13px", border: "1px solid rgba(76,175,80,0.3)" },
  cancelledMsg: { background: "rgba(233,69,96,0.1)", color: "var(--accent)", padding: "8px 14px", borderRadius: "8px", fontSize: "13px", border: "1px solid rgba(233,69,96,0.3)" },
  noShowMsg: { background: "rgba(171,71,188,0.1)", color: "#ab47bc", padding: "8px 14px", borderRadius: "8px", fontSize: "13px", border: "1px solid rgba(171,71,188,0.3)" },
};